import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { PdfPageReader } from '@/components/PdfPageReader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudyGuideViewer() {
  const { id } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStudyGuide();
  }, [id]);

  const loadStudyGuide = async () => {
    try {
      const response = await axios.get(`${API}/study-guides/${id}`);
      setGuide(response.data);
    } catch (error) {
      console.error('Failed to load study guide:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <div className="text-2xl text-[#6B4423]">Loading study guide...</div>
        </div>
      </Layout>
    );
  }

  if (!guide) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl retro-title text-[#2C1810] mb-4">Study Guide Not Found</h1>
          <Link to="/study-guide-center" className="retro-button">
            Back to Study Guides
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/study-guide-center" className="inline-flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-4">
            <ArrowLeft className="w-5 h-5" />
            Back to Study Guides
          </Link>
          <h1 className="text-4xl retro-title text-[#2C1810] mb-2">{guide.test_title}</h1>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-[#8B5A3C] text-white text-sm font-bold rounded">
              {guide.tier}
            </span>
            <span className="px-3 py-1 bg-[#E8934D] text-white text-sm font-bold rounded">
              {guide.category}
            </span>
            <span className="text-[#6B4423]">
              <BookOpen className="w-4 h-4 inline mr-1" />
              {guide.source_format === 'pdf_upload' ? `${guide.page_image_count} pages` : `${guide.sections?.length || 0} sections`}
            </span>
          </div>
        </div>

        {/* Study Guide Content */}
        <div className="max-w-4xl mx-auto space-y-8">
          {guide.source_format === 'pdf_upload' ? (
            <PdfPageReader imageBaseUrl={`${API}/study-guides/${id}`} pageCount={guide.page_image_count} />
          ) : (
            <>
          {guide.sections && guide.sections.map((section, index) => (
            <div key={index} className="cassette-card p-8">
              {/* Section Heading */}
              <div className="border-b-4 border-[#E8934D] pb-4 mb-6">
                <h2 className="text-3xl retro-title text-[#2C1810]">
                  {section.heading}
                </h2>
              </div>

              {/* Section Content */}
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-[#2C1810] mt-8 mb-4" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-[#2C1810] mt-6 mb-3" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-xl font-bold text-[#2C1810] mt-4 mb-2" {...props} />,
                    p: ({node, ...props}) => <p className="text-[#3D2817] mb-4 leading-relaxed" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2 text-[#3D2817]" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-2 text-[#3D2817]" {...props} />,
                    li: ({node, ...props}) => <li className="ml-4" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-[#2C1810]" {...props} />,
                    em: ({node, ...props}) => <em className="italic text-[#6B4423]" {...props} />,
                    code: ({node, ...props}) => <code className="bg-[#F4E4C1] px-2 py-1 rounded text-[#2C1810] font-mono text-sm" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-[#E8934D] pl-4 italic text-[#6B4423] my-4" {...props} />
                  }}
                >
                  {section.content}
                </ReactMarkdown>
              </div>
            </div>
          ))}

          {/* Empty state */}
          {(!guide.sections || guide.sections.length === 0) && (
            <div className="cassette-card p-12 text-center">
              <BookOpen className="w-16 h-16 text-[#6B4423] mx-auto mb-4 opacity-50" />
              <p className="text-lg text-[#6B4423]">No sections available in this study guide.</p>
            </div>
          )}
          </>
          )}
        </div>

        {/* Print Button */}
        <div className="mt-12 text-center">
          <button
            onClick={() => window.print()}
            className="retro-button"
          >
            Print Study Guide
          </button>
        </div>
      </div>
    </Layout>
  );
}
